// Hosts a microlearning video embed may point at. A host matches itself and its subdomains
// (e.g. customer-xxxx.cloudflarestream.com).
export const ALLOWED_EMBED_DOMAINS = [
  "api.video",
  "cloudflarestream.com",
  "videodelivery.net",
  "youtube.com",
  "youtube-nocookie.com",
  "vimeo.com",
  "loom.com",
];

export const EMBED_SANDBOX = "allow-scripts allow-presentation";

export const EMBED_ALLOW =
  "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen";

function isHostOrSubdomain(hostname, domain) {
  return hostname === domain || hostname.endsWith(`.${domain}`);
}

export function isAllowedEmbedHost(hostname) {
  if (!hostname || typeof hostname !== "string") return false;
  const host = hostname.toLowerCase();
  return ALLOWED_EMBED_DOMAINS.some((domain) => isHostOrSubdomain(host, domain));
}

function extractSrcCandidate(raw) {
  const value = raw.trim();
  if (!/<iframe/i.test(value)) return value;
  // DOMParser documents are inert: nothing in them loads or runs. Only the first iframe's src is
  // read; every other attribute, srcdoc and sibling element is discarded.
  const doc = new DOMParser().parseFromString(value, "text/html");
  return (doc.querySelector("iframe")?.getAttribute("src") || "").trim();
}

/**
 * Resolves a stored video value (bare URL or iframe embed snippet) to a safe https embed URL.
 * Returns null when it does not resolve to an allowlisted provider.
 */
export function parseEmbedSource(raw) {
  if (!raw || typeof raw !== "string") return null;
  const candidate = extractSrcCandidate(raw);
  if (!candidate) return null;

  let url;
  try {
    url = new URL(candidate.startsWith("//") ? `https:${candidate}` : candidate);
  } catch {
    return null;
  }

  if (url.protocol !== "https:" || url.username || url.password) return null;
  return isAllowedEmbedHost(url.hostname) ? url.href : null;
}

/** Adds autoplay (and unmuted playback for api.video) without re-encoding the existing query. */
export function withAutoplay(href) {
  const url = new URL(href);
  if (url.searchParams.has("autoplay")) return href;
  const extra = isHostOrSubdomain(url.hostname, "api.video")
    ? "autoplay=true&muted=false"
    : "autoplay=true";
  url.search = url.search ? `${url.search}&${extra}` : `?${extra}`;
  return url.href;
}
